import React, { useState, useEffect } from 'react';
import { getInvestments } from '../services/api';
import { TrendingUp, ExternalLink, MoreHorizontal } from 'lucide-react';

const InvestmentsSection = () => {
  const [angelInvestments, setAngelInvestments] = useState([]);
  const [crowdfunding, setCrowdfunding] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getInvestments();
        const investments = data.data || [];
        
        // Separate angel and crowdfunding investments
        const angels = investments.filter(inv => inv.type === 'angel');
        // Put "Others" at the end
        const sortedAngels = [
          ...angels.filter(inv => inv.name !== 'Others'),
          ...angels.filter(inv => inv.name === 'Others')
        ];
        setAngelInvestments(sortedAngels);
        setCrowdfunding(investments.filter(inv => inv.type === 'crowdfunding'));
      } catch (error) {
        console.error('Error fetching investments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="py-20 bg-slate-900">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Get initials for fallback
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="py-16 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
              <TrendingUp size={24} className="text-white" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-white">
            Venture Portfolio
          </h2>
          <p className="text-base text-slate-400 font-light">
            Backing founders building the future
          </p>
        </div>

        <div className="max-w-6xl mx-auto space-y-12">
          
          {/* Angel Investments - Featured */}
          {angelInvestments.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-lg font-bold text-amber-500">Angel Investments</span>
                <div className="flex-1 h-px bg-gradient-to-r from-amber-500/50 to-transparent" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {angelInvestments.map((investment, index) => (
                  investment.name === 'Others' ? (
                    <div
                      key={index}
                      className="bg-slate-800/40 backdrop-blur-sm rounded-xl p-5 border border-slate-700/50 flex items-center justify-center"
                    >
                      <div className="text-center">
                        <div className="w-12 h-12 rounded-lg bg-slate-700/30 flex items-center justify-center mx-auto mb-3 border border-slate-600/30">
                          <MoreHorizontal size={20} className="text-slate-400" />
                        </div>
                        <h4 className="font-semibold text-slate-400 text-sm mb-1">+ Others</h4>
                        <p className="text-slate-500 text-xs">More early-stage investments</p>
                      </div>
                    </div>
                  ) : (
                    <a
                      key={index}
                      href={investment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-slate-800/70 backdrop-blur-sm rounded-xl p-5 border border-slate-700 hover:border-amber-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-500/10"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-lg bg-slate-700/50 flex items-center justify-center overflow-hidden border border-slate-600 group-hover:border-amber-500/30 transition-colors flex-shrink-0">
                          {investment.logo ? (
                            <img 
                              src={investment.logo} 
                              alt={investment.name}
                              className="w-8 h-8 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full flex items-center justify-center text-amber-400 font-bold text-sm ${investment.logo ? 'hidden' : ''}`}>
                            {getInitials(investment.name)}
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-white text-sm group-hover:text-amber-400 transition-colors truncate">
                            {investment.name}
                          </h4>
                          <span className="text-amber-500/80 text-xs font-medium">
                            {investment.sector}
                          </span>
                        </div>
                        
                        <ExternalLink size={14} className="text-slate-500 group-hover:text-amber-400 transition-colors flex-shrink-0" />
                      </div>
                      
                      <p className="text-slate-400 text-xs line-clamp-2 leading-relaxed">
                        {investment.description}
                      </p>
                    </a>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Crowdfunding Investments - Condensed initials grid */}
          {crowdfunding.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="text-lg font-bold text-slate-400">Crowdfunding via Republic</span>
                <div className="flex-1 h-px bg-gradient-to-r from-slate-600/50 to-transparent" />
              </div>
              
              {/* Compact grid - company names only */}
              <div className="flex flex-wrap gap-2 justify-center">
                {crowdfunding.map((investment, index) => (
                  <a
                    key={index}
                    href={investment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/50 hover:border-amber-500/30 hover:bg-slate-700/60 transition-all duration-300"
                  >
                    <span className="text-slate-400 text-xs font-medium group-hover:text-amber-400 transition-colors">
                      {investment.name}
                    </span>
                  </a>
                ))}
              </div>
              
              {/* View All on Republic CTA */}
              <div className="text-center mt-6">
                <a
                  href="https://republic.com/@nishit-sadhwani"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700/50 text-slate-300 font-medium text-sm rounded-lg hover:bg-slate-700 transition-all duration-300 group border border-slate-600/50"
                >
                  View Republic Profile
                  <ExternalLink size={14} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentsSection;
